import React, { useEffect, useState } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';

// Lists every user by name. Picking one previews the site as that person.
export default function ViewAsToggle({ inline = false }) {
  const { canViewAs, realRole, realUser, viewAsUser, setViewAsUser } = useAuth();
  const [people, setPeople] = useState([]);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!canViewAs) return;
    let cancelled = false;
    get(ref(database, 'users'))
      .then((snap) => {
        if (cancelled) return;
        const isAdmin = realRole === 'Admin';
        const list = Object.entries(snap.val() || {})
          .map(([uid, u]) => ({
            uid,
            displayName: u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'Unnamed user',
            email: u.email || '',
            role: u.role || 'Agent',
          }))
          .filter((p) => p.uid !== realUser?.uid) // never list yourself: "Me" is already the first option
          .filter((p) => isAdmin || p.role === 'Agent')
          .sort((a, b) => a.displayName.localeCompare(b.displayName));
        setPeople(list);
      })
      .catch((err) => {
        console.error('Could not load users for view as:', err);
        if (!cancelled) setLoadError('Could not load users');
      });
    return () => { cancelled = true; };
  }, [canViewAs, realRole, realUser?.uid]);

  if (!canViewAs) return null;

  const viewing = !!viewAsUser;

  const pick = (uid) => {
    if (!uid) { setViewAsUser(null); return; }
    const person = people.find((p) => p.uid === uid);
    if (person) setViewAsUser(person);
  };

  return (
    <div className={inline ? 'viewas-inline' : 'viewas-floating'} style={{ ...(inline ? inlineWrap : wrap), borderColor: viewing ? '#e3c35f' : '#b8962e' }}>
      <span style={label}>View as</span>
      <select
        value={viewAsUser?.uid || ''}
        onChange={(e) => pick(e.target.value)}
        style={inline ? { ...select, maxWidth: 'none', flex: 1, minHeight: 44, fontSize: 16 } : select}
        aria-label="View as user"
      >
        <option value="">Me ({realRole || 'no role'})</option>
        {loadError && <option disabled>{loadError}</option>}
        {people.map((p) => (
          <option key={p.uid} value={p.uid}>{p.displayName} ({p.role})</option>
        ))}
      </select>
    </div>
  );
}

const wrap = {
  position: 'fixed',
  bottom: 16,
  right: 16,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 10px',
  backgroundColor: '#1a1a1a',
  border: '1px solid #b8962e',
  borderRadius: 8,
  boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
};
const inlineWrap = { display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 8, padding: '8px 10px', backgroundColor: '#0a0a0a', border: '1px solid #b8962e', borderRadius: 8, marginTop: 8 };
const label = { fontSize: 11, color: '#e3c35f', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: 700 };
const select = {
  backgroundColor: '#0a0a0a',
  color: '#FFFFFF',
  border: '1px solid #333333',
  borderRadius: 4,
  padding: '6px 8px',
  fontSize: 13,
  cursor: 'pointer',
  maxWidth: 220,
};
